import styles from "@/styles/featureDeals.module.scss"
import Head from "next/head"
import DefaultLayout from "@/components/DefaultLayout"
import SliderBarLeft from "@/components/SliderBarLeft"
import SliderBarRight from "@/components/SliderBarRight"
import ShopInfo from "@/components/ShopInfo"
import BreadCrum from '@/components/global/BreadCrum'
import GoodsComponentWithDiscountNew from '@/components/GoodsComponentWithDiscountNew'
import CategoryFilter from "@/components/search/CategoryFilter"
import FilterItem from "@/components/search/FilterItem"
import NewArrivals from "@/components/category/NewArrivals"
import GoogleADSense from "@/components/GoogleADSense"
import type { PaginationProps } from 'antd';
import { Pagination, Spin } from 'antd';

import dayjs from 'dayjs'
import { debounce } from 'lodash';
import { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'
import { setCookie } from '@/utils/cookie'
import featuredDealsApi from '@/service/modules/featuredDealsApi'
import featuredSectionApi from '@/service/modules/featuredSectionApi'

interface ProductItem {
  productID: string;
  name: string;
  url: string;
  imgUrl: string;
  reviewCount: number;
  price: number;
  normalPrice: number;
  freeShipping: number;
  onSale: number;
  reduced: number;
  isNew: number;
  position?: number,
  location?: string;
  isEmailOnly: any;
  inStock?: number;
  isGroup?: number;
  promotionTypeID: string | number;
  promotion: {
    productID: number;
    promotionType: number;
    promotionPrice: number;
    normalPrice: number;
    endDate: string;
    status: number;
    notified: number;
    pointRate: string;
    promotionRate: number;
  };
  review: {
    avgRank: number;
    total: number;
  };
  shortDescription?: string;
  visitor?: {
    weeklyVisitors: number;
  }
  rankNum?: number;
}

interface BreadcrumbItem {
  name: string;
  url: string;
  current: boolean;
}

// 定义分类项接口
interface sideFilterItem {
  name: string;
  description?: string;
  count?: number;
  sortBy: string;
  direction: string;
  minPrice?: number | null;
  maxPrice?: number | null;
}

interface filterTypeItem {
  title: string;
  key: string;
  currentItemName?: string;
  items: sideFilterItem[]
}

// 分类筛选接口
interface cateFilterItem {
  name: string;
  categoryID: number | string;
  url: string;
  subCategories?: cateFilterItem[];
}

interface cateFilterType {
  title: string;
  key: string;
  currentCategoryId?: string;
  items: cateFilterItem[]
}

// interface Param {
//   categoryID: number | string;
//   currentCategoryName: string;
//   currentCategoryID: number | string;
// }

// 获取分类列表数据
async function getSearchData(params: any) {
  try {
    const dataObj = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null && value !== '')
    );
    let productData = await featuredDealsApi.getFeaturedDealsData(dataObj)

    if (
      productData.data 
      && productData.data.pageList 
      && productData.data.pageList.items
      && productData.data.pageList.items.length
    ) {
      productData.data.pageList.items.forEach((item: any) => {
        item.imgUrl = item.filename || item.newThumbnail
      })
    }

    return {
      ...productData.data
    }
  } catch (error) {
    console.log('Next.js Error :', dayjs().format('YY-MM-DD HH:mm:ss'), `  Page Feature Deals: /search/${params.keyword}/ Data acquisition failed:`, JSON.stringify(error))
  }
}

export default function PageFeaturedDeals() {
  const router = useRouter()

  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [updating, setUpdating] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(48);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageProductData, setPageProductData] = useState<ProductItem[]>([]);
  // const [pageParams, setPageParams] = useState<Param>({
  //   categoryID: 0,
  //   currentCategoryName: '',
  //   currentCategoryID: 0
  // });

  const [breadCrumb, setBreadCrumb] = useState<BreadcrumbItem[]>([]);
  const [categoryId, setCategoryId] = useState<number | string>('');

  const [sidebarFilter, setSidebarFilter] = useState<filterTypeItem[]>([]);
  const [sidebarCateFilter, setSidebarCateFilter] = useState<cateFilterType[]>([]);

  const [categoryName, setCategoryName] = useState('');
  const [aboutCategory, setAboutCategory] = useState('');
  const [pageSize] = useState(48)

  // 获取页面About数据
  async function getCategoryExtraData(_: string | number) {
    try {
      let resData = await featuredSectionApi.getAboutDetail('featured-deals')

      if (resData.data && resData.data) {
        setCategoryName(resData.data?.name || '')
        setAboutCategory(resData.data?.description || '')
      }
    } catch (error) {
      console.log(`feature deals页面about数据获取失败： `, error)
    }
  }

  // 获取页面数据(主要是初始化请求，数据筛选的请求在 handleUpdateData)
  const getPageData = async() => {
    try {
      // 开启页面loading
      setUpdating(true)

      const data = await getSearchData({ 
        category: '',
        page: 1,
        perPage: 48,
        sortBy: 'bestMatch',
        sortDirection: 'desc' 
      })

      setUpdating(false)
  
      if (data) {
        setCategoryId(data.categoryID)
  
        // setPageParams(data.param || {
        //   categoryID: 0,
        //   currentCategoryName: '',
        //   currentCategoryID: 0
        // })

        setBreadCrumb(data.breadCrumb || [])

        if (page != 1) setPage(1)
        setLastPage(data.pageList.lastPage)
        setPerPage(data.pageList.perPage)
        setTotal(data.pageList.total)
        setPageProductData(data.pageList.items)

        // 获取侧边栏数据
        getSidebarFilterData()

        // 获取分类页面about数据
        getCategoryExtraData(data.categoryID)
      }
    } catch (error) {
      setUpdating(false)
    }
  }

  // 获取侧边栏数据
  const getSidebarFilterData = async(params?: any, sidebarFilter?: filterTypeItem[]) => {
    try {
      setIsFirstLoad(true)
      let dataObj = {}

      // 优先使用筛选带过来的参数
      if (params) dataObj = params
      let { data } = await featuredDealsApi.getSidebarFilter('featured-deals', dataObj)

      if (data && data.sidebarFilter) {
        let categoryOriginalData = data.sidebarFilter && data.sidebarFilter.length ? data.sidebarFilter.filter((item: any) => item.key == 'category') : []
        let categoryFilterData = JSON.parse(JSON.stringify(categoryOriginalData))
        if (params) {
          categoryFilterData[0].currentCategoryId = params.categoryID || ''
        } else {
          categoryFilterData[0].currentCategoryId = ''
        }

        let filtersData = data.sidebarFilter.filter((item: any) => item.key != 'category')
        filtersData.forEach((item: any) => {
          let target: filterTypeItem[] = []
          if (sidebarFilter) {
            target = sidebarFilter.filter((v: filterTypeItem) => item.key == v.key)

            // 如果当前有选中，但是数据回来没有，把当前选中数据赋值进items
            if ((!item.items || item.items.length <= 0) && target.length) {
              let saveItems = target[0].items.filter((s: any) => s.name == target[0].currentItemName)
              item.items = JSON.parse(JSON.stringify(saveItems))
            }
          }
          if (item.key && target.length) {
            item.currentItemName = target[0].currentItemName || ''
          } else {
            item.currentItemName = ''
          }
        })

        // 过滤掉没有数据的分类
        let finnalFiltersData = filtersData.filter((item: any) => item.items && item.items.length)

        setSidebarCateFilter(categoryFilterData || [])
        setSidebarFilter(finnalFiltersData || [])
      }

      // 延迟关闭页面loading
      setTimeout(() => {
        setIsFirstLoad(false)
      }, 200);
    } catch (error) {
      console.log(error, ' == error');
    }
  }

  /**
   * 初始化请求页面数据
   * 1、判断url中是否有aid参数
   * 2、如果没有aid就直接请求
   * 3、包含aid参数，就延迟一些请求(等待setCookie)
   */
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    if (aid || recoveraid) {
      setTimeout(() => {
        getPageData()
      }, 100)
    } else {
      getPageData()
    }
  }, [])

  // 判断是否有aid 或 recoveraid在url，有就将其
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    let lastAidValue = ''
    let lastRecoveraidValue = ''

    // 检查是否有"aid"参数，如有多个取最后
    if (Array.isArray(aid) && aid.length > 0) {
      lastAidValue = aid[aid.length - 1]
    } else if (typeof aid === 'string') {
      // 如果只有一个"aid"参数，它将是一个字符串
      lastAidValue = aid
    }

    // 检查是否有"recoveraid"参数，如有多个取最后
    if (Array.isArray(recoveraid) && recoveraid.length > 0) {
      lastRecoveraidValue = recoveraid[recoveraid.length - 1]
    } else if (typeof recoveraid === 'string') {
      // 如果只有一个"recoveraid"参数，它将是一个字符串
      lastRecoveraidValue = recoveraid
    }

    // 优先于异步请求，将aid存入cookie, 60秒过期（后续会发起请求重新设置）
    if (lastAidValue || lastRecoveraidValue) {
      let currentDomain = window.location.hostname;
      if(currentDomain == 'www.crazysales.com.au') {
        currentDomain = ".crazysales.com.au"
      }
      setCookie('affiliateid', lastAidValue || lastRecoveraidValue, { expires: 30, secure: true, domain: `${currentDomain}`, })
    }
  }, [router.query])

  // 分页器Change事件
  const onChange: PaginationProps['onChange'] = (current) => {
    setPage(current)
  };

  // sidebar category change事件
  const handleFilterSelectCategory = (categoryItem: cateFilterItem) => {
    /**
     * 如果当前分类筛选与点选的分类相同，则取消分类筛选
     * 分类不相同，则增加分类筛选
     * 跳转使用router.push的方式，并且加上shallow配置，url变化，但页面是静态刷新
     */
    let currentSidebarCateFilter = JSON.parse(JSON.stringify(sidebarCateFilter))
    const currentCategoryId = currentSidebarCateFilter[0].currentCategoryId

    // 支持反选
    if (currentCategoryId == categoryItem.categoryID) {
      currentSidebarCateFilter[0].currentCategoryId = ''
    } else {
      currentSidebarCateFilter[0].currentCategoryId = categoryItem.categoryID
    }

    setSidebarCateFilter(currentSidebarCateFilter)
  }

  // sidebar filter 点击事件
  const handleFilterItemSelect = (filterKey: string, itemName: string) => {
    /**
     * 接收到参数
     * 按顺序来筛选，hot brand => hot search => price range
     * 举例： 选了hot search 可以叠加 price range的参数，但是选了 price range 再选 hot search 会把 price range的条件剔除
     */

    let filterData = JSON.parse(JSON.stringify(sidebarFilter))

    switch (filterKey) {
      case 'hot-brand':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'hot-brand') {
            if (item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      case 'custom-filtering':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'custom-filtering') {
            if (item.currentItemName != itemName && item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      case 'price-range':
        filterData.forEach((item: filterTypeItem) => {
          if (item.key == 'price-range') {
            if (item.currentItemName != itemName && item.currentItemName != itemName) {
              item.currentItemName = itemName
            } else {
              item.currentItemName = ''
            }
          }
        });
        break
      default:
        // 默认，把参数剔除
        filterData.forEach((item: filterTypeItem) => {
          item.currentItemName = ''
        });
        break
    }

    setPage(1)
    setSidebarFilter(filterData)
  }

  // 更新页面数据
  const handleUpdateData = async (params: any, sidebarFilter: filterTypeItem[]) => {
    try {
      if (updating) return
      setUpdating(true)

      // 如果没有参数，则默认排序
      if (!params.sortBy) {
        params.sortBy = 'bestMatch'
        params.sortDirection = 'desc'
      }
      const data = await getSearchData(params)

      // 结束loading，给100ms的延迟，防止页面抖动
      setTimeout(() => {
        setUpdating(false)
      }, 100)

      if (data) {
        if (data.link && data.link.replaceUrl) {
          window.location.href = data.link.replaceUrl
        }

        setLastPage(data.pageList.lastPage)
        setPerPage(data.pageList.perPage)
        setTotal(data.pageList.total)
        setPageProductData(data.pageList.items)
  
        setBreadCrumb(data.breadCrumb)

        // 如果筛选的结果，有数据，则更新侧边筛选条件
        getSidebarFilterData(params, sidebarFilter)
      }
    } catch (error) {
      setUpdating(false)
    }
  }

  // 处理请求参数
  const handleQueryParams = (
    page: number, 
    pageSize: number,
    sidebarCateFilter: cateFilterType[],
    sidebarFilter: filterTypeItem[]
  ) => {
    let dataObj = { 
      page,
      perPage: pageSize,
      categoryID: '',
      brand: '',
      sortBy: '',
      sortDirection: '',
      minPrice: '',
      maxPrice: ''
    }

    if (sidebarCateFilter && sidebarCateFilter.length && sidebarCateFilter[0].currentCategoryId) {
      dataObj.categoryID = sidebarCateFilter[0].currentCategoryId
    }

    let filterData = JSON.parse(JSON.stringify(sidebarFilter))

    filterData.forEach((item: filterTypeItem) => {
      if (item.currentItemName) {
        switch (item.key) {
          case 'hot-brand':
            dataObj.brand = item.currentItemName
            break
          case 'custom-filtering': {
            let target = item.items.filter((v: sideFilterItem) => v.name + v.description == item.currentItemName)
            if (target.length) {
              if (target[0].sortBy) dataObj.sortBy = String(target[0].sortBy)
              if (target[0].direction) dataObj.sortDirection = String(target[0].direction)
            }
            break
          }
          case 'price-range': {
            let target = item.items.filter((v: sideFilterItem) => v.name == item.currentItemName)
            if (target.length) {
              if (target[0].minPrice) dataObj.minPrice = String(target[0].minPrice)
              if (target[0].maxPrice) dataObj.maxPrice = String(target[0].maxPrice)
            }
            break
          }
          default:
            break
        }
      }
    });

    // 剔除无用的参数
    let paramsObj = Object.fromEntries(
      Object.entries(dataObj).filter(([_, value]) => value !== null && value !== '')
    )

    return paramsObj
  }

  // 定义一个去抖函数，延迟300ms执行
  const debouncedFetchProducts = useCallback(
    debounce(async (page, pageSize, sidebarCateFilter, sidebarFilter) => {
      try {
        let params = handleQueryParams(page, pageSize, sidebarCateFilter, sidebarFilter)
        handleUpdateData(params, sidebarFilter)
      } catch (error) {
        console.error('Failed to fetch products', error);
      }
    }, 200),
    [] // 如果筛选条件和分页状态是不会变的，可以把它们包含到依赖数组中。否则保持数组为空。
  );

  // 当筛选条件或分页状态变化时执行去抖后的数据获取函数
  useEffect(() => {
    // 判断不是第一次获取数据，才去请求更新
    if (!isFirstLoad) {
      debouncedFetchProducts(page, pageSize, sidebarCateFilter, sidebarFilter);
    }
  }, [page, pageSize, sidebarCateFilter, sidebarFilter]);

  return (
    <>
      <Head>
        <title>Online Shopping Australia - Beauty & Health, Computers, Toys, - Deals & Discounts | CrazySales</title>
        <meta name="keywords" content={`Online Shopping Australia, online shopping sites, Home Shopping, Shopping Deals, Best Deals, Home Garden, Pet Supplies, Sports Fitness, Bedding Bath, Buy Online, Discount Shopping, Toys Hobbies, Beauty Health, Crazy Sales, Computers, IT, Christmas, Electronics, Automotive, Tools, Appliances, Baby, CrazySales`}></meta>
        <meta name="description" content={`CrazySales.com.au - Australia's #1 online shopping website! Cheap bargains & discounts with the top online shopping Australia store! From furniture, to toys & hobbies, to appliances & pet supplies -- we've got it all, with free shipping on selected items!`}></meta>
      </Head>

      <DefaultLayout notStickyNavbar={true}>
        <div className={`${styles['page-search']} slider-wapper`}>
          <SliderBarLeft></SliderBarLeft>
          <SliderBarRight></SliderBarRight>

          {/* 面包屑 */}
          {
            !!(breadCrumb && breadCrumb.length) &&
            <BreadCrum BreadList={breadCrumb}></BreadCrum>
          }

          {/* 商城特色介绍 */}
          <div className={styles['shop-info-box']}>
            <ShopInfo hiddenContactus={true}></ShopInfo>
          </div>

          <div className={styles['page-container']}>
            <div className={styles['filter-box']}>

              <Spin spinning={updating}>
                {/* 其他后端返回的筛选 */}
                {
                  !!(sidebarFilter && sidebarFilter.length) &&
                  <div className={styles['filter-list']}>
                    {
                      sidebarFilter.map((item: filterTypeItem) => (
                        <div className={styles['filter-item']} key={item.key}>
                          <FilterItem 
                            filterTypeItem={item}
                            onItemSelect={handleFilterItemSelect}
                          ></FilterItem>
                        </div>
                      ))
                    }
                  </div>
                }
                
                {/* 分类的筛选 */}
                <div className={styles['category-filter']}>
                  <div className={styles['category-filter-content']}>
                    <CategoryFilter
                      onlyFirstLevelCategory
                      cateFilterData={sidebarCateFilter}
                      onSelectCategory={handleFilterSelectCategory}
                    ></CategoryFilter>
                  </div>
                </div>
              </Spin>

              {/* 新品上市或者热门商品推荐 */}
              {
                !!(categoryId) && 
                <div className={styles['new-arrivals']}>
                  <NewArrivals 
                    type={'hotProducts'}
                    category={categoryId}
                    title={'Hot Products'}
                  ></NewArrivals>
                </div>
              }

              {/* 广告 */}
              <div style={{ marginTop: '20px' }}>
                <GoogleADSense />
              </div>
            </div>

            <div className={styles['container-box']}>
              <Spin spinning={updating}>
                <div className={`${styles['product-list']} margin-top-0`}>
                  {
                    pageProductData && pageProductData.length
                    ?
                    pageProductData.map((product: ProductItem, index: number) => (
                      <div className={styles['product-item']} key={index}>
                        <GoodsComponentWithDiscountNew {...{ ...product, productName: product.name, imgUrl: product.imgUrl }} />
                      </div>
                    ))
                    :
                    <div className={styles['product-empty']}>Sorry, no products available with the selected options.</div>
                  }
                </div>

                {/* 分页器 */}
                {
                  !!(total && lastPage > 1) &&
                  <div className={styles['pagination-box']}>
                    <Pagination
                      key={perPage * total}
                      showQuickJumper
                      showSizeChanger={false}
                      current={page}
                      onChange={onChange}
                      defaultPageSize={perPage}
                      total={total}
                    />
                  </div>
                }
              </Spin>

              {
                !!(aboutCategory) &&
                <div className={`${styles['about-box']} margin-top-16`}>
                  <div className={styles['header']}>
                    About&nbsp;{categoryName}
                  </div>

                  <div className={styles['content']} dangerouslySetInnerHTML={{ __html: aboutCategory }}></div>
                </div>
              }
              
              <div style={{ marginTop: '20px' }}>
                <GoogleADSense />
              </div>
            </div>
          </div>

        </div>
      </DefaultLayout>
    </>
  )
}
